'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { createPortal } from 'react-dom';
import { langNames, langFlags } from './lang-meta';
import type { Lang, Plan, SectionVisibility, TranslationKeys } from './types';
import type { SiteCopy, DynamicNavItem } from '@/lib/marketing/site-copy';
import { LOCALE_COOKIE } from '@/lib/i18n';
import { stripLocalePrefix } from '@/lib/i18n/href';

// Persist the landing's chosen language as the app-wide locale cookie so
// downstream pages (login, signup, dashboard) pick up the same language.
const writeLocaleCookie = (lang: Lang) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${LOCALE_COOKIE}=${lang};path=/;max-age=${60 * 60 * 24 * 365};samesite=lax`;
};

// Above-the-fold: static imports (fast, small, no framer-motion)
import NavBar from './sections/NavBar';
import MobileSidebar from './sections/MobileSidebar';
import HeroSection from './sections/HeroSection';
import BuildingForSection from './sections/BuildingForSection';
import TrustBadges from './sections/TrustBadges';
import FooterSection from './sections/FooterSection';
import { CallCenterWidgetLoader } from '@/components/callcenter-widget-loader';

// Below-the-fold: dynamic imports (code-split, SSR enabled for faster initial paint)
const FeaturesSection = dynamic(() => import('./sections/FeaturesSection'));
const BuiltForSection = dynamic(() => import('./sections/BuiltForSection'));
const HowItWorksSection = dynamic(() => import('./sections/HowItWorksSection'));
const PricingSection = dynamic(() => import('./sections/PricingSection'));
const FaqSection = dynamic(() => import('./sections/FaqSection'));
const CtaSection = dynamic(() => import('./sections/CtaSection'));

interface LandingClientProps {
  // Translations for the CURRENT locale only. Both `t` (static strings) and
  // `copy` (CMS-managed overrides) are resolved by the server for the URL
  // locale and passed as sub-trees, so the client bundle never ships the
    // other 4 locales' ~150KB of dead noise. Language switching updates the locale route without keeping all locale
    // maps in the client bundle.
  t: TranslationKeys;
  copy?: SiteCopy;
  navTree?: DynamicNavItem[];
  // Server-side prefetch — passing these in lets us seed the initial state
  // with the right values and skip the client-side fetch, which used to
  // produce a visible layout shift (pricing cards popped in ~400ms after
  // first paint, FAQ + CTA jumped down). When the parent doesn't pass them
  // (legacy callers), we still fall back to the old in-component fetch.
  initialPlans?: Plan[];
  initialSections?: SectionVisibility;
}

function readLocaleFromPath(pathname: string | null): Lang {
  const seg = (pathname || '/').split('/', 2)[1];
  return (['en', 'es', 'pt', 'de', 'fr'] as const).includes(seg as Lang) ? (seg as Lang) : 'en';
}

export default function LandingClient({ t, copy, navTree, initialPlans, initialSections }: LandingClientProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [isYearly, setIsYearly] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [lang, setLang] = useState<Lang>(() => readLocaleFromPath(pathname));
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [plans, setPlans] = useState<Plan[]>(initialPlans ?? []);
  const [planStartIndex, setPlanStartIndex] = useState(0);
  const [sections, setSections] = useState<SectionVisibility>(initialSections ?? {
    showFeatures: true,
    showHowItWorks: true,
    showPricing: true,
    showFaq: true,
    showTrustBadges: true,
  });
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [logoFxActive, setLogoFxActive] = useState(false);
  const [logoFxGrow, setLogoFxGrow] = useState(false);
  const [logoFxFlash, setLogoFxFlash] = useState(false);
  const [logoFxPhase, setLogoFxPhase] = useState<'start' | 'pop' | 'settle' | 'fade'>('start');
  const [videoModalReady, setVideoModalReady] = useState(false);
  const [logoFxStyle, setLogoFxStyle] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    // URL is the source of truth now — middleware redirected /? no-prefix
    // visits to /{detected}/ before we even rendered, so `lang` already
    // matches the locale in the path. Still mirror into the cookie and
    // localStorage so legacy consumers (verify/*, settings) pick it up.
    const resolvedLang = readLocaleFromPath(pathname);
    writeLocaleCookie(resolvedLang);
    try { localStorage.setItem('lang', resolvedLang); } catch {}

    // Plans + section visibility are seeded server-side now (page.tsx passes
    // them as props). Only fall back to client-side fetch when the parent
    // didn't provide them, so legacy mount paths keep working. When the
    // parent DOES provide them, `useState(initialPlans)` only captures the
    // first-mount value — a locale switch (router.push + router.refresh)
    // re-renders this component with a new `initialPlans` prop but that
    // alone never updates `plans` state, so cards stayed stuck in the old
    // locale until a hard reload. Re-sync explicitly whenever it changes.
    if (!initialPlans) {
      fetch(`/api/payments/plans?locale=${encodeURIComponent(resolvedLang)}`)
        .then(res => res.json())
        .then(data => {
          if (data.status === 200 && data.plans) setPlans(data.plans);
        })
        .catch(err => console.error('Error fetching plans:', err));
    } else {
      setPlans(initialPlans);
    }
    if (!initialSections) {
      fetch('/api/platform')
        .then(res => res.json())
        .then(data => {
          if (data.status === 200 && data.settings) {
            setSections({
              showFeatures: data.settings.showFeatures ?? true,
              showHowItWorks: data.settings.showHowItWorks ?? true,
              showPricing: data.settings.showPricing ?? true,
              showFaq: data.settings.showFaq ?? true,
              showTrustBadges: data.settings.showTrustBadges ?? true,
            });
          }
        })
        .catch(err => console.error('Error fetching platform settings:', err));
    } else {
      setSections(initialSections);
    }
  }, [pathname, initialPlans, initialSections]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const previousOverflow = document.body.style.overflow;
    if (videoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = previousOverflow || '';
    }
    return () => {
      document.body.style.overflow = previousOverflow || '';
    };
  }, [videoModalOpen]);

  const handleLangChange = (newLang: Lang) => {
    setLang(newLang);
    localStorage.setItem('lang', newLang);
    writeLocaleCookie(newLang);
    setLangMenuOpen(false);
    const bare = stripLocalePrefix(pathname || '/');
    const target = bare === '/' ? `/${newLang}` : `/${newLang}${bare}`;
    router.push(target, { scroll: false });
    router.refresh();
  };

  const openHowItWorksVideo = () => {
    if (typeof window === 'undefined') return;
    const initialSize = 112;
    const top = window.innerHeight / 2 - initialSize / 2;
    const left = window.innerWidth / 2 - initialSize / 2;

    setLogoFxStyle({
      top,
      left,
      width: initialSize,
      height: initialSize,
    });
    setLogoFxGrow(false);
    setLogoFxFlash(false);
    setVideoModalReady(false);
    setVideoModalOpen(false);
    setLogoFxPhase('start');
    setLogoFxActive(true);

    window.setTimeout(() => {
      setLogoFxPhase('pop');
    }, 20);
    window.setTimeout(() => {
      setLogoFxPhase('settle');
    }, 420);
    window.setTimeout(() => {
      setLogoFxPhase('fade');
      setLogoFxFlash(true);
    }, 620);

    window.setTimeout(() => {
      setVideoModalOpen(true);
      setVideoModalReady(true);
      setLogoFxActive(false);
      setLogoFxGrow(false);
      setLogoFxFlash(false);
      setLogoFxStyle(null);
    }, 920);
  };

  // `copy` is already the current-locale sub-tree (set by the server from
  // getSiteCopy(locale)), so there's nothing to index here anymore.

  return (
    <>
      <NavBar
        t={t}
        lang={lang}
        sections={sections}
        langMenuOpen={langMenuOpen}
        setLangMenuOpen={setLangMenuOpen}
        onLangChange={handleLangChange}
        setMobileMenuOpen={setMobileMenuOpen}
        dynamicNav={navTree}
      />
      <MobileSidebar
        t={t}
        lang={lang}
        sections={sections}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onLangChange={handleLangChange}
      />
      <main id="main-content" className="min-h-screen bg-white overflow-x-clip scrollbar-hide relative">
        <HeroSection t={t} copy={copy} onOpenHowItWorksVideo={openHowItWorksVideo} />
        <BuildingForSection t={t} />
        <TrustBadges t={t} />
        {sections.showFeatures && <FeaturesSection t={t} copy={copy} />}
        <BuiltForSection t={t} />
        {sections.showHowItWorks && <HowItWorksSection t={t} />}
        {sections.showPricing && (
          <PricingSection
            t={t}
            plans={plans}
            isYearly={isYearly}
            setIsYearly={setIsYearly}
            planStartIndex={planStartIndex}
            setPlanStartIndex={setPlanStartIndex}
            copy={copy}
            lang={lang}
          />
        )}
        {sections.showFaq && <FaqSection t={t} copy={copy} />}
        <CtaSection t={t} copy={copy} />
      </main>
      {logoFxActive && logoFxStyle && (
        <div className="fixed inset-0 z-[95] pointer-events-none" aria-hidden="true">
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${
              logoFxFlash ? 'bg-white opacity-100' : 'bg-white/50 opacity-70'
            }`}
          />
          <img
            src="/images/brand/ezdocuai-logo.png"
            alt=""
            className={`absolute object-contain ${
              logoFxPhase === 'start'
                ? 'scale-[0.3] opacity-100 transition-none'
                : logoFxPhase === 'pop'
                ? 'scale-[1.2] opacity-100 transition-transform duration-[400ms] ease-out'
                : logoFxPhase === 'settle'
                ? 'scale-100 opacity-100 transition-transform duration-[200ms] ease-in'
                : 'scale-[0.8] opacity-0 transition-all duration-[300ms] ease-in'
            }`}
            style={logoFxStyle}
          />
        </div>
      )}
      {videoModalOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[2147483647] bg-white/70 backdrop-blur-sm flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={t.videoTitle}
        >
          <div className="flex flex-col h-full">
              <div className="flex items-center justify-between px-4 md:px-8 py-4 shrink-0 bg-white/95 border-b border-black/10">
                <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setVideoModalOpen(false)}
                  className="h-10 w-10 rounded-md border border-[#ab5af6]/60 flex cursor-pointer items-center justify-center text-[#ab5af6] hover:border-[#ab5af6] hover:bg-[#ab5af6]/10 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ab5af6]"
                  aria-label={t.videoClose}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
                  <div className="ml-3 hidden sm:block">
                    <p className="text-sm font-bold uppercase text-[#ab5af6] tracking-wider leading-tight">
                      {t.tutorialVideos}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <img
                    src="/images/brand/ezdocuai-logo.png"
                    alt=""
                    aria-hidden="true"
                    className="h-8 w-8 object-contain"
                  />
                  <span className="text-[9px] tracking-[0.12em] text-black/50 mt-0.5">EZDocuAI</span>
                </div>
              </div>

              <div className="flex-1 px-2 md:px-8 pb-2 md:pb-8 min-h-0">
              <iframe
                src="https://www.youtube.com/embed/G0hlUUR3M30?autoplay=1&rel=0"
                title={t.videoTitle}
                  className={`h-full w-full border-0 transition-all duration-300 ${
                  videoModalReady ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-6 scale-[0.85]'
                }`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
              </div>
            </div>
        </div>,
        document.body
      )}
      <FooterSection t={t} />
      {/* Hide the floating support bubble while the mobile menu is open — the
          widget's own high z-index otherwise floats it on top of the menu. */}
      {!mobileMenuOpen && <CallCenterWidgetLoader locale={lang} />}
    </>
  );
}
